import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription, throwError, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// INTERFACES
import {
  OrderReturnInterface,
  OrderReturnItemInterface,
  OrderSalesInterface,
  OrderSalesItemInterface,
  OrderReturnFormInterface,
} from '../../../../order';
import { IPagination } from '../../../interfaces';

// SERVICES
import { EnvironmentUtilService } from '../../util';
import { ApiService } from '../api.service';

@Injectable({
  providedIn: 'root',
})
export class OrderReturnApiService {
  private readonly _orderReturns = new BehaviorSubject<OrderReturnInterface[]>(
    <OrderReturnInterface[]>[],
  );
  private readonly _currentParentSalesOrder =
    new BehaviorSubject<OrderSalesInterface>(<OrderSalesInterface>{});
  private readonly _currentParentSalesOrderItems = new BehaviorSubject<
    OrderSalesItemInterface[]
  >(<OrderSalesItemInterface[]>[]);
  private readonly _currentOrderReturn = new BehaviorSubject<OrderReturnInterface>(
    <OrderReturnInterface>{},
  );
  private readonly _currentOrderReturnItems = new BehaviorSubject<
    OrderReturnItemInterface[]
  >(<OrderReturnItemInterface[]>[]);

  private readonly _pagination = new BehaviorSubject<IPagination>(<IPagination>{});
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _submittingForm = new BehaviorSubject<boolean>(<boolean>false);

  orderReturns$ = this._orderReturns.asObservable();

  currentParentSalesOrder$ = this._currentParentSalesOrder.asObservable();
  currentParentSalesOrderItems$ = this._currentParentSalesOrderItems.asObservable();
  currentOrderReturn$ = this._currentOrderReturn.asObservable();
  currentOrderReturnItems$ = this._currentOrderReturnItems.asObservable();
  pagination$ = this._pagination.asObservable();
  loading$ = this._loading.asObservable();
  submittingForm$ = this._submittingForm.asObservable();

  environment = this.environmentUtilService.getEnvironment();

  constructor(
    private apiService :ApiService,
    private environmentUtilService :EnvironmentUtilService,
  ) {}

  getOrderReturnsByInvoiceId(data :{
    params :{ [param :string] :string | string[] };
  }) :Subscription {
    const { params = {} } = data;
    this._loading.next(true);

    return this.apiService
      .get(`${this.environment.urlV2}/orders/returns`, {
        params,
      })
      .subscribe({
        next: (response) => {
          this._orderReturns.next(response.data);
          this._pagination.next(response.meta);
          this._loading.next(false);
        },
        error: (error) =>
          throwError(() => {
            this._loading.next(false);

            return new Error(error);
          }),
      });
  }

  getOrderReturnById(data :{
    orderReturnId :number;
    params ?:{ [param :string] :string | string[] };
  }) :Subscription {
    const { orderReturnId, params = {} } = data;
    this._loading.next(true);

    return this.apiService
      .get(`${this.environment.urlV2}/orders/returns/${orderReturnId}`, {
        params,
      })
      .subscribe({
        next: (response) => {
          this._currentOrderReturn.next(response);
          this._currentOrderReturnItems.next(response.orderItems);
          this._loading.next(false);
        },
        error: (error) =>
          throwError(() => {
            this._loading.next(false);

            return new Error(error);
          }),
      });
  }

  getParentSalesOrderById(data :{
    parentSalesOrderId :number;
    params ?:{ [param :string] :string | string[] };
  }) :Subscription {
    const { parentSalesOrderId, params = {} } = data;
    this._loading.next(true);

    return this.apiService
      .get(`${this.environment.urlV2}/orders/sales/${parentSalesOrderId}`, {
        params,
      })
      .subscribe({
        next: (response) => {
          this._currentParentSalesOrder.next(response);
          this._currentParentSalesOrderItems.next(response.orderItems);
          this._loading.next(false);
        },
        error: (error) =>
          throwError(() => {
            this._loading.next(false);

            return new Error(error);
          }),
      });
  }

  returnOrder(data :OrderReturnFormInterface) :Observable<any> {
    const {
      salesOrderId,
      orderDate,
      restock,
      customRefundAmount,
      restockingFee,
      items,
    } = data;

    this._submittingForm.next(true);

    return this.apiService
      .post({
        path: `${this.environment.urlV2}/orders/returns`,
        body: {
          salesOrderId,
          orderDate,
          restock,
          customRefundAmount,
          restockingFee,
          items,
        },
      })
      .pipe(
        map((response :any) => {
          this._submittingForm.next(false);

          return response;
        }),
        catchError((response) => {
          this._submittingForm.next(false);

          return throwError(() => response.error);
        }),
      );
  }
}
